import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { RestService } from '@cumlaude/shared-services';
import {
	CronWorkflow,
	InstellingBron,
	RAuditType,
	RInstelling,
	RJobAudit,
	SubmitWorkflow,
	SubmitWorkflowParam,
	Workflow,
	WorkflowTemplate,
} from '@cumlaude/service-contract';
import { AuthService } from '@cumlaude/shared-authentication';
import { dateDiffInDays, getNow } from '@cumlaude/shared-utils';

@Injectable({
	providedIn: 'root',
})
export class EtlService {
	private readonly restService = inject(RestService);
	protected readonly authService = inject(AuthService);

	async activateCronWorkflow(cronWorkflow: CronWorkflow) {
		const objectMeta = cronWorkflow.metadata;
		const name = objectMeta.name;
		await firstValueFrom(this.restService.resumeCronWorkflow(name));

		await this.auditWorkflow(RAuditType.CRON_ACTIVATED, undefined, name, objectMeta.uid);
	}

	async deactivateCronWorkflow(cronWorkflow: CronWorkflow) {
		const objectMeta = cronWorkflow.metadata;
		const name = objectMeta.name;
		await firstValueFrom(this.restService.suspendCronWorkflow(name));

		await this.auditWorkflow(RAuditType.CRON_DEACTIVATED, undefined, name, objectMeta.uid);
	}

	async startLandelijkeWorkflow(templateName: string) {
		const workflow = await firstValueFrom(
			this.restService.submitWorkflow({
				name: templateName.replace('-template-manual', ''),
				templateName: templateName,
			})
		);

		if (!workflow) return false;

		const metadata = workflow.metadata;
		await this.auditWorkflow(RAuditType.WORKFLOW_START, undefined, metadata?.name, metadata?.uid);

		return true;
	}

	async startInstellingWorkflow(templateName: string, bulk: boolean, instelling: RInstelling) {
		return this.startInstellingenWorkflow(templateName, bulk, [instelling]);
	}

	async startInstellingenWorkflow(templateName: string, bulk: boolean, instellingen?: RInstelling[], parallelism?: number) {
		const parameters: SubmitWorkflowParam[] = [
			{
				name: 'env-is-bulkload',
				value: bulk ? '1' : '0',
			},
			{
				name: 'jobPool',
				value: bulk ? 'regular' : 'spot',
			},
		];

		if (instellingen) {
			const instellingIds = instellingen.map((instelling) => instelling.id).join(';');
			parameters.push({
				name: 'env-inst-pk-keys',
				value: instellingIds,
			});
		}

		const submitWorkflow: SubmitWorkflow = {
			name: templateName.replace('-template-manual', ''),
			templateName,
			parameters,
			parallelism,
		};

		const workflow = await firstValueFrom(this.restService.submitWorkflow(submitWorkflow));
		if (!workflow) return false;

		const metadata = workflow.metadata;
		await this.auditWorkflow(RAuditType.WORKFLOW_START, undefined, metadata?.name, metadata?.uid);

		return true;
	}

	mapWorkflowTemplate(workflowTemplate: WorkflowTemplate, runningWorkflows: Workflow[] = []) {
		const workflowTemplateName = workflowTemplate.metadata.name;

		let bron = undefined;
		if (workflowTemplateName.includes('somtoday-job')) bron = InstellingBron.Somtoday;
		else if (workflowTemplateName.includes('magister-job')) bron = InstellingBron.Magister;

		const workflowName = workflowTemplateName.replace('-job-template-manual', '');

		const runningWorkflow = !bron && runningWorkflows.some((workflow) => workflow.metadata.name.includes(workflowName));

		return {
			uid: workflowTemplate.metadata.uid,
			name: workflowName.replace('-', ' '),
			bron,
			workflowTemplate,
			runningWorkflow,
		};
	}

	async toggleBulkETL(instelling: RInstelling) {
		const nextETLBulk = !instelling.nextETLBulk;
		const resultaat = await firstValueFrom(
			this.restService.putInstelling({
				...instelling,
				nextETLBulk,
			})
		);

		if (!resultaat) return undefined;

		await this.auditWorkflow(nextETLBulk ? RAuditType.BULK_SCHEDULED : RAuditType.BULK_UNSCHEDULED, resultaat);

		return resultaat;
	}

	private async auditWorkflow(auditType: RAuditType, instelling?: RInstelling, workflowName?: string, workflowUid?: string) {
		const userDisplayName = this.authService.getUserDisplayName();
		const jobAudit = <RJobAudit>{
			$type: 'etl.RJobAudit',
			auditType: auditType,
			instelling,
			workflowName,
			workflowUid,
			createdAt: getNow(),
			createdBy: userDisplayName,
		};
		await firstValueFrom(this.restService.postJobAudit(jobAudit));
	}
}

export function doorlooptijdFromMs(diffInMs: number) {
	const diffInSeconds = Math.floor(diffInMs / 1000);

	const hours = Math.floor(diffInSeconds / 3600);
	const minutes = Math.floor((diffInSeconds % 3600) / 60);
	const seconds = diffInSeconds % 60;

	const parts = [];
	if (hours > 0) {
		parts.push(`${hours}u`, `${minutes}m`, `${seconds}s`);
	} else if (minutes > 0) {
		parts.push(`${minutes}m`, `${seconds}s`);
	} else {
		parts.push(`${seconds}s`);
	}
	return parts.join(' ');
}

export function doorlooptijdInMs(sourceVa: string | undefined, sourceTm: string | undefined, jobrunTm?: string): number | undefined {
	if (!sourceVa) return undefined;

	const now = new Date();
	const vanaf = new Date(sourceVa);
	const days = dateDiffInDays(vanaf, now);
	if (days > 0 && !sourceTm && !jobrunTm) return undefined;

	let totMet = now;
	if (sourceTm) totMet = new Date(sourceTm);
	else if (jobrunTm) totMet = new Date(jobrunTm);

	return totMet.getTime() - vanaf.getTime();
}

export function doorlooptijd(sourceVa: string | undefined, sourceTm: string | undefined, jobrunTm?: string) {
	const diffInMs = doorlooptijdInMs(sourceVa, sourceTm, jobrunTm);
	if (diffInMs === undefined) return '-';

	return doorlooptijdFromMs(diffInMs);
}
